import React, { useState, useCallback } from 'react'
import { FlatList } from 'react-native'
import { connect } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { TouchableRipple, Checkbox } from 'react-native-paper/lib/module'
import styled, { useTheme } from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import Config from 'APP/Config'
import I18n from 'APP/I18n'
import PatientActions from 'APP/Redux/PatientRedux'

import { Images } from 'APP/Themes'
import ChallengedInvitationFooter from 'APP/Components/ChallengedInvitationFooter'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import Typography from 'APP/Converse/Typography'
import Header from 'APP/Converse/Header'
import LogoHeader from 'APP/Components/LogoHeader'
import Button from 'APP/Converse/Button'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const InvitationContainer = styled.View`
  background-color: ${({ theme }) => theme.colors.elementsBg};
  ${({ theme }) => theme.shadows.soft};
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin / 4}px;
  border-radius: ${({ theme }) => theme.metrics.borderRadius.small}px;
`

const InvitationContentContainer = styled.View`
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

// Workaround - because separators only add space between items (good), but then the
// container doesnt grow to account for the shadows around each item.
const ItemSeparator = styled.View`
  height: ${({ theme }) => theme.metrics.baseMargin - theme.metrics.baseMargin / 4}px;
`

export function buildInvitationList(searchResults = []) {
  const invitations = [...searchResults]

  if (Config.SUPPORTED_CHALLENGED_ENROLMENT_METHOD) {
    invitations.push({
      id: 'claim_access',
      name: I18n.t('ChallengedEnrolmentsSearchScreen.otherOrganization'),
    })
  }

  return invitations.map((invitation) => ({
    invitation,
    checked: false,
  }))
}

const ChallengedEnrolmentsSearchScreen = ({
  analyticsName,
  searchResult,
  challengedInvitationQueueRequest,
  isOnboarding,
  onboardingDeferredEnrolmentComplete,
}) => {
  const theme = useTheme()
  const navigation = useNavigation()
  const [invitationList, setInvitationList] = useState(buildInvitationList(searchResult))

  const handleNext = () => {
    // Filter out the invitations that are not checked and extract the invite for use
    const selectedInvitations = invitationList.reduce((selected, item) => {
      if (item.checked) {
        selected.push(item.invitation)
      }
      return selected
    }, [])
    challengedInvitationQueueRequest(selectedInvitations)
  }

  const handleSkipForNow = () => {
    if (isOnboarding) {
      onboardingDeferredEnrolmentComplete()
    } else {
      navigation.goBack()
    }
  }

  const handleInvitationPressed = useCallback(
    (item) => {
      const newList = invitationList.map((i) => {
        if (i.invitation.id === item.invitation.id) {
          return {
            ...i,
            checked: !i.checked,
          }
        }
        return i
      })

      setInvitationList(newList)
    },
    [invitationList]
  )

  const renderInvitation = ({ item }) => {
    const name =
      item.invitation.id === 'claim_access'
        ? item.invitation.name
        : I18n.serverString(item?.invitation?.organization?.displayName)

    return (
      <InvitationContainer>
        <TouchableRipple onPress={() => handleInvitationPressed(item)}>
          <InvitationContentContainer>
            <Checkbox.Android
              color={theme.colors.text}
              uncheckedColor={theme.colors.text}
              status={item.checked ? 'checked' : 'unchecked'}
            />
            <Typography variant="subheader">{name}</Typography>
          </InvitationContentContainer>
        </TouchableRipple>
      </InvitationContainer>
    )
  }

  return (
    <ScreenContainer>
      <ResponsiveScrollView>
        <Header hideBackButton={!isOnboarding} />
        <ContentContainer>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('ChallengedEnrolmentsSearchScreen.title')}
            subtitle={I18n.t('ChallengedEnrolmentsSearchScreen.subtitle')}
          />

          <FlatList
            data={invitationList}
            keyExtractor={(item) => item?.invitation?.id ?? item?.invitation?.organization?.name}
            renderItem={renderInvitation}
            ItemSeparatorComponent={ItemSeparator}
          />

          <Button
            variant="primary"
            widthVariant="full"
            title={I18n.t('ChallengedEnrolmentsSearchScreen.nextButton')}
            analyticsName={`${analyticsName}.next`}
            onPress={handleNext}
            testID="NEXT_BTN"
          />
          <ChallengedInvitationFooter analyticsName={analyticsName} onPress={handleSkipForNow} />
        </ContentContainer>
      </ResponsiveScrollView>
    </ScreenContainer>
  )
}

const mapStateToProps = (state, props) => {
  const { patient } = state
  return {
    ...props.route?.params,
    isOnboarding: patient?.isOnboarding,
  }
}

const mapDispatchToProps = (dispatch) => ({
  challengedInvitationQueueRequest: (queue) =>
    dispatch(PatientActions.challengedInvitationQueueRequest(queue)),
  onboardingDeferredEnrolmentComplete: () =>
    dispatch(PatientActions.onboardingDeferredEnrolmentComplete()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ChallengedEnrolmentsSearchScreen)
